import React, { useEffect, useCallback } from "react";
import Select, { ActionMeta } from "react-select";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "src/redux";
import {
  setInvoiceFieldStandard,
  setInvoiceFieldExtra,
  setLineItemFieldStandard,
  setLineItemFieldExtra,
} from "src/redux/reducers/InvoiceManager";
import { setReloadExportPreview } from "src/redux/reducers/DownloadInvoiceOptions";

interface FieldOption {
  value: string;
  label: string;
  group: "standard" | "extra";
}

interface FieldSelectorProps {}

const STORAGE_KEY = "invoice-export-selected-fields";

export const InvoiceFieldsSelector: React.FC<FieldSelectorProps> = ({}) => {
  const {
    downloadOptions,
    transactionLines,
    invoiceFieldStandard,
    invoiceFieldExtra,
    lineItemFieldStandard,
    lineItemFieldExtra,
  } = useSelector((state: ReduxState) => state.InvoiceManager);

  const dispatch = useDispatch();

  // Set default values when component mounts
  useEffect(() => {
    // Only set defaults if no fields are currently selected
    if (invoiceFieldStandard.length === 0 && invoiceFieldExtra.length === 0) {
      dispatch(setInvoiceFieldStandard(availableFields.regular));
    }
    if (lineItemFieldStandard.length === 0 && lineItemFieldExtra.length === 0) {
      dispatch(setLineItemFieldStandard(availableFields.lineItem));
    }
  }, []); // Empty dependency array means this runs once on mount

  const availableFields = {
    regular: [
      "id",
      "date",
      "dueDate",
      "total",
      "tax",
      "poNumber",
      "vendor",
      "customer",
      "account",
      "fileName",
      "pageNumber",
    ],
    extra: [
      ...new Set(
        transactionLines.flatMap((tl) => {
          try {
            const rowData = JSON.parse((tl.row as any) || "{}");
            return Object.keys(rowData.extraFields || {});
          } catch {
            return [];
          }
        })
      ),
    ],
    lineItem: [
      "item",
      "quantity",
      "unitPrice",
      "price",
      "tax",
      "category",
      "service",
    ],
    lineItemExtra: [
      ...new Set(
        transactionLines.flatMap((tl) => {
          try {
            const rowData = JSON.parse((tl.row as any) || "{}");
            return (rowData.lines || []).flatMap((line) =>
              Object.keys(line.extraFields || {})
            );
          } catch {
            return [];
          }
        })
      ),
    ],
  };

  // User-friendly labels for invoice standard fields (backend expects the value keys)
  const invoiceFieldLabels: Record<string, string> = {
    fileName: "File Name",
    pageNumber: "Page Number",
  };

  // Memoize the options for invoice fields
  const invoiceOptions = React.useMemo(
    () => [
      {
        label: "Standard Fields",
        options: availableFields.regular.map((field) => ({
          value: field,
          label: invoiceFieldLabels[field] ?? field,
          group: "standard",
        })),
      },
      {
        label: "Extra Fields",
        options: availableFields.extra.map((field) => ({
          value: field,
          label: field,
          group: "extra",
        })),
      },
    ],
    [availableFields]
  );

  // Memoize the options for line item fields
  const lineItemOptions = React.useMemo(
    () => [
      {
        label: "Standard Fields",
        options: availableFields.lineItem.map((field) => ({
          value: field,
          label: field,
          group: "standard",
        })),
      },
      {
        label: "Extra Fields",
        options: availableFields.lineItemExtra.map((field) => ({
          value: field,
          label: field,
          group: "extra",
        })),
      },
    ],
    [availableFields]
  );

  // Memoize handlers for invoice fields
  const handleInvoiceSelectAll = useCallback(() => {
    const allInvoiceFields = [
      ...availableFields.regular,
      ...availableFields.extra,
    ];
    dispatch(setInvoiceFieldStandard(availableFields.regular));
    dispatch(setInvoiceFieldExtra(availableFields.extra));
  }, [dispatch, availableFields]);

  const handleInvoiceSelectNone = useCallback(() => {
    dispatch(setInvoiceFieldStandard([]));
    dispatch(setInvoiceFieldExtra([]));
  }, [dispatch]);

  // Memoize handlers for line item fields
  const handleLineItemSelectAll = useCallback(() => {
    const allLineItemFields = [
      ...availableFields.lineItem,
      ...availableFields.lineItemExtra,
    ];
    dispatch(setLineItemFieldStandard(availableFields.lineItem));
    dispatch(setLineItemFieldExtra(availableFields.lineItemExtra));
  }, [dispatch, availableFields]);

  const handleLineItemSelectNone = useCallback(() => {
    dispatch(setLineItemFieldStandard([]));
    dispatch(setLineItemFieldExtra([]));
  }, [dispatch]);

  const handleInvoiceChange = useCallback(
    (
      selected: readonly FieldOption[] | null | undefined,
      actionMeta: ActionMeta<FieldOption>
    ) => {
      const newFields = selected ? selected.map((option) => option.value) : [];
      const standardFields = newFields.filter((field) =>
        availableFields.regular.includes(field)
      );
      const extraFields = newFields.filter((field) =>
        availableFields.extra.includes(field)
      );
      dispatch(setInvoiceFieldStandard(standardFields));
      dispatch(setInvoiceFieldExtra(extraFields));
    },
    [dispatch, availableFields]
  );

  const handleLineItemChange = useCallback(
    (
      selected: readonly FieldOption[] | null | undefined,
      actionMeta: ActionMeta<FieldOption>
    ) => {
      const newFields = selected ? selected.map((option) => option.value) : [];
      const standardFields = newFields.filter((field) =>
        availableFields.lineItem.includes(field)
      );
      const extraFields = newFields.filter((field) =>
        availableFields.lineItemExtra.includes(field)
      );
      dispatch(setLineItemFieldStandard(standardFields));
      dispatch(setLineItemFieldExtra(extraFields));
    },
    [dispatch, availableFields]
  );

  // Save to localStorage when selection changes, but debounce it
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const allSelectedFields = [
        ...invoiceFieldStandard,
        ...invoiceFieldExtra.map((field) => `extra.${field}`),
        ...lineItemFieldStandard.map((field) => `lineItem.${field}`),
        ...lineItemFieldExtra.map((field) => `lineItem.extra.${field}`),
      ];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allSelectedFields));
    }, 300); // Debounce for 300ms

    return () => clearTimeout(timeoutId);
  }, [
    invoiceFieldStandard,
    invoiceFieldExtra,
    lineItemFieldStandard,
    lineItemFieldExtra,
  ]);

  return (
    <div className="mb-3">
      <div className="mb-4">
        <Form.Label className="mb-0">
          <strong>Invoice Fields</strong>
        </Form.Label>
        <div className="mb-2">
          <button
            type="button"
            className="btn btn-link btn-sm mr-2"
            onClick={handleInvoiceSelectAll}
          >
            Select All
          </button>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={handleInvoiceSelectNone}
          >
            Select None
          </button>
        </div>
        <Select<FieldOption, true>
          isMulti
          options={invoiceOptions as any}
          value={
            invoiceOptions
              .flatMap((group) => group.options)
              .filter((option) =>
                [...invoiceFieldStandard, ...invoiceFieldExtra].includes(
                  option.value as string
                )
              ) as any
          }
          onChange={handleInvoiceChange}
          placeholder="Search and select invoice fields..."
          className="basic-multi-select"
          classNamePrefix="select"
        />
      </div>

      <div>
        <Form.Label className="mb-0">
          <strong>Line Item Fields</strong>
        </Form.Label>
        <div className="mb-2">
          <button
            type="button"
            className="btn btn-link btn-sm mr-2"
            onClick={handleLineItemSelectAll}
          >
            Select All
          </button>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={handleLineItemSelectNone}
          >
            Select None
          </button>
        </div>
        <Select<FieldOption, true>
          isMulti
          options={lineItemOptions as any}
          value={
            lineItemOptions
              .flatMap((group) => group.options)
              .filter((option) =>
                [...lineItemFieldStandard, ...lineItemFieldExtra].includes(
                  option.value as string
                )
              ) as any
          }
          onChange={handleLineItemChange}
          placeholder="Search and select line item fields..."
          className="basic-multi-select"
          classNamePrefix="select"
        />
      </div>
    </div>
  );
};
